<script>
  import Chart from './Chart.svelte'
  import { fetchWithTimeout } from '../../common/hydrographHelper.js'
  import { inview } from 'svelte-inview'
  import { isInView, nwmLoaded } from './store'
  import { refresh } from './store.js'
  refresh.set(false)

  export let gauge
  export let stageflow
  export let gaugeConfig // gauge configuration as JSON
  export let assetURL = ''
  export let lid
  export let chartName
  export let reachID
  export let view
  export let ratings = null // added for Chrome warning

  let gaugePromise = {}
  let options = {}
  var intervalID
  var intervalRunning = false
  //Updates to unique will trigger recreating the hydrograph
  let unique = {}
  //HG will refresh every 5 min in ms
  let refreshInterval = 300000

  //Triggers refresh function on change
  $: $refresh, refreshHG()

  async function getGauge() {
    if (lid === null) return null
    try {
      let url = `${OWP_API_URL}${GAUGE_API_RESOURCE}gauges/${lid}`

      const response = await fetch(url)
      const gauge = await response.json()

      if (response.ok) {
        return gauge
      } else {
        throw new Error(gauge.message)
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.error(`abort for url :${url}`)
      } else {
        console.error(`error for url :${url}, ${err}`)
      }
    }
  }

  async function getRatings() {
    if (lid === null) return null
    try {
      let url = `${OWP_API_URL}${GAUGE_API_RESOURCE}gauges/${lid}/ratings`
      let response = await fetchWithTimeout(url, {
        // milliseconds
        timeout: 30000,
      })
      const ratings = await response.json()
      if (response.ok) {
        return ratings
      }
      return []
    } catch (err) {
      if (error.name === 'AbortError') {
        console.error(`abort for url :${url}`)
      } else {
        console.error(`error for url :${url}, ${error}`)
      }
    }
  }

  async function getStageflow() {
    if (lid === null) return null
    try {
      //let url = OWP_API_URL + GAUGE_API_RESOURCE + lid + '/stageflow'
      let url = `${OWP_API_URL}${GAUGE_API_RESOURCE}gauges/${lid}/stageflow`

      const response = await fetch(url)
      const stageflow = await response.json()

      if (response.ok) {
        return stageflow
      } else {
        throw new Error(stageflow.message)
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.error(`abort for url :${url}`)
      } else {
        console.error(`error for url :${url}, ${err}`)
      }
    }
  }

  async function getGaugeConfig() {
    if (lid === null) return null
    try {
      let url = OWP_WEB_URL + '/map/config?lid=' + encodeURIComponent(lid)

      const response = await fetch(url)
      const gaugeConf = await response.json()

      if (response.ok) {
        return gaugeConf
      } else {
        throw new Error(gaugeConf.message)
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.error(`abort for url :${url}`)
      } else {
        console.error(`error for url :${url}, ${err}`)
      }
    }
  }

  $: {
    if (stageflow == undefined && gauge == undefined && $isInView === true && !$nwmLoaded) {
      gaugePromise = Promise.all([getGauge(), getRatings(), getStageflow(), getGaugeConfig()])
      $nwmLoaded = true
    } else if ($isInView === true && !$nwmLoaded) {
      gaugePromise = Promise.all([getRatings()])
      $nwmLoaded = true
    }
  }

  function refreshHG() {
    if ($refresh == true) {
      intervalID = setInterval(function () {
        if (intervalRunning == true) return
        // short circuit if the hydrograph isn't in view
        if ($isInView == false) return
        intervalRunning = true
        //Check if refresh has been disabled on each iteration
        if ($refresh == false) {
          clearInterval(intervalID)
          intervalRunning = false
          return
        }
        //Get updated Data
        gaugePromise = Promise.all([getGauge(), getRatings(), getStageflow(), getGaugeConfig()])
        unique = {}
        intervalRunning = false
      }, refreshInterval)
    }
  }
  clearInterval(intervalID)
</script>

<div>
  {#if stageflow == undefined && gauge == undefined}
    {#key unique}
      {#await gaugePromise}
        <div class="loading uk-inline">
          <div class="uk-position-center">Loading...</div>
        </div>
      {:then [gauge, ratings, stageflow, gaugeConfig]}
        <div
          use:inview={options}
          on:inview_change={(event) => {
            const { inView, entry, scrollDirection, observer, node } = event.detail
            $isInView = inView
          }}
          on:inview_enter={(event) => {
            const { inView, entry, scrollDirection, observer, node } = event.detail
            $isInView = inView
          }}
          on:inview_leave={(event) => {
            const { inView, entry, scrollDirection, observer, node } = event.detail
            $isInView = inView
          }}
          on:inview_init={(event) => {
            const { observer, node } = event.detail
          }}
        >
          <Chart
            {view}
            {reachID}
            {chartName}
            {lid}
            ratingsCurve={ratings}
            {stageflow}
            {gauge}
            {assetURL}
            {gaugeConfig}
          />
        </div>
      {:catch error}
        <div class="loading uk-inline">
          <div class="uk-position-center">Hydrograph unavailable for this site...</div>
        </div>
      {/await}
    {/key}
  {:else}
    {#key unique}
      {#await gaugePromise}
        <div class="loading uk-inline">
          <div class="uk-position-center">Loading...</div>
        </div>
      {:then [gauge, ratings, stageflow, gaugeConfig]}
        <div
          use:inview={options}
          on:inview_change={(event) => {
            const { inView, entry, scrollDirection, observer, node } = event.detail
            $isInView = inView
          }}
          on:inview_enter={(event) => {
            const { inView, entry, scrollDirection, observer, node } = event.detail
            $isInView = inView
          }}
          on:inview_leave={(event) => {
            const { inView, entry, scrollDirection, observer, node } = event.detail
            $isInView = inView
          }}
          on:inview_init={(event) => {
            const { observer, node } = event.detail
          }}
        >
          <Chart
            {view}
            {reachID}
            {chartName}
            {lid}
            ratingsCurve={ratings}
            {stageflow}
            {gauge}
            {assetURL}
            {gaugeConfig}
          />
        </div>
      {:catch error}
        <div class="loading uk-inline">
          <div class="uk-position-center">Hydrograph unavailable for this site...</div>
        </div>
      {/await}
    {/key}
  {/if}
</div>

<style>
  .loading {
    width: 100%;
    min-height: 200px;
    color: #fff;
  }

  /* Window is SMALLER than UIkit large breakpoint */
  @media screen and (max-width: 1200px) {
    :global(.move-to-top-below-s) {
      order: -1;
    }
    :global(.flood-thresholds > div) {
      border-right: none !important;
    }
  }
</style>
